import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import Loader from "@/components/loader";
import Button from "@/components/ui/Button";
import { canSaveAiRenderAsAsset } from "./aiRender";

// Sits on top of the live viewport (see Scene3DEditor.jsx) rather than in a
// modal, so the in-progress/finished AI render can be compared directly
// against the 3D scene it came from. Only mounted while the "scene vs.
// render" toggle (also in Scene3DEditor.jsx, outside this component) is set
// to "render" — this component itself just decides which of busy/error/
// result to show.
export default function AiRenderOverlay({
  phase,
  errorMessage,
  renders,
  currentIndex,
  onPrev,
  onNext,
  onSaveAsAsset,
  savingAsset,
  onCreateProjection,
  canCreateProjection,
  creatingProjection,
  onBackToScene,
}) {
  const busy = phase === "rendering" || phase === "generating";
  const current = renders[currentIndex];

  if (!busy && phase !== "error" && !current) return null;

  return (
    <div
      id="AiRenderOverlay"
      className="absolute inset-0 z-20 flex items-center justify-center">
      {busy && (
        <Loader
          status={
            phase === "rendering"
              ? "Rendering scene..."
              : "Generating AI render..."
          }
        />
      )}

      {phase === "error" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-background/95 text-center px-4">
          <span className="text-xs text-destructive-foreground">
            {errorMessage}
          </span>
          <button
            id="AiRenderOverlay__closeTrigger"
            onClick={onBackToScene}
            className="text-xs text-secondary-foreground hover:text-white cursor-pointer underline">
            Close
          </button>
        </div>
      )}

      {phase === "idle" && current && (
        <div className="absolute inset-0 flex flex-col bg-black">
          <div className="relative flex-1 min-h-0 flex items-center justify-center">
            <img
              src={current.url}
              alt="AI render result"
              className="max-w-full max-h-full object-contain"
            />
            {renders.length > 1 && (
              <>
                <button
                  id="AiRenderOverlay__prevTrigger"
                  type="button"
                  onClick={onPrev}
                  disabled={currentIndex >= renders.length - 1}
                  title="Older render"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-foreground hover:text-white disabled:opacity-30 disabled:cursor-default cursor-pointer bg-background/70 rounded-full p-2">
                  <FiChevronLeft className="w-5 h-5" />
                </button>
                <button
                  id="AiRenderOverlay__nextTrigger"
                  type="button"
                  onClick={onNext}
                  disabled={currentIndex <= 0}
                  title="Newer render"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-foreground hover:text-white disabled:opacity-30 disabled:cursor-default cursor-pointer bg-background/70 rounded-full p-2">
                  <FiChevronRight className="w-5 h-5" />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-[10px] text-secondary-foreground/70 bg-background/70 rounded-md px-2 py-0.5">
                  {currentIndex + 1} / {renders.length}
                </div>
              </>
            )}
          </div>
          <div className="absolute top-0 right-0 flex justify-center gap-2 p-3 ">
            <Button
              id="AiRenderOverlay__saveAsAssetTrigger"
              type="secondary"
              onClick={onSaveAsAsset}
              disabled={savingAsset || !canSaveAiRenderAsAsset(current)}>
              {!canSaveAiRenderAsAsset(current)
                ? "Saved"
                : savingAsset
                  ? "Saving..."
                  : "Save as Asset"}
            </Button>
            <Button
              id="AiRenderOverlay__createProjectionTrigger"
              type="secondary"
              onClick={onCreateProjection}
              disabled={creatingProjection || !canCreateProjection}
              title={
                canCreateProjection
                  ? "Bake this render onto the scene as a projected texture"
                  : "Scene changed since this render — projection unavailable"
              }>
              {creatingProjection ? "Creating..." : "Create Projection"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
